// advanceOn types:
//   { type: "route", path }  — auto-advance when user navigates to this path
//   { type: "click" }        — advance when user clicks the highlighted element
//   { type: "event", name }  — advance when window.dispatchEvent fires this name
//
// noBackdrop: true  — no spotlight panels (step requires free form interaction)
// cardPosition:
//   "right"        — card right of element, arrow points left
//   "below"        — card below element, arrow points up
//   "fixed-corner" — card fixed at bottom-right (no arrow; used for modals/forms)

export const TOUR_STEPS = [

  // ── TASK 1: Rubrica Fornitori ─────────────────────────────────────────────

  {
    id: "nav-suppliers",
    selector: '[data-tour-step="nav-suppliers"]',
    cardPosition: "right",
    icon: "BookText",
    advanceOn: { type: "route", path: "/suppliers" },
    it: {
      title: "Inizia dalla Rubrica Fornitori",
      body: "La rubrica è il punto di partenza di tutto. Aggiungi i tuoi fornitori una sola volta e potrai usarli in qualsiasi evento, senza reinserire i dati ogni volta.",
      cta: "Clicca su Fornitori nel menu",
    },
    en: {
      title: "Start with the Supplier Directory",
      body: "The directory is your starting point. Add your suppliers once and reuse them across every event without entering data again.",
      cta: "Click on Suppliers in the menu",
    },
  },
  {
    id: "add-supplier-btn",
    selector: '[data-tour-step="add-supplier-btn"]',
    cardPosition: "below",
    icon: "Plus",
    advanceOn: { type: "click" },
    it: {
      title: "Aggiungi il primo fornitore",
      body: "Ogni fornitore che aggiungi qui sarà disponibile in tutti i tuoi eventi futuri. Niente più copie, niente più fogli sparsi.",
      cta: "Clicca su Nuovo fornitore",
    },
    en: {
      title: "Add your first supplier",
      body: "Every supplier you add here will be available across all your future events. No more copies, no more scattered spreadsheets.",
      cta: "Click on New supplier",
    },
  },
  {
    id: "supplier-form",
    selector: '[data-tour-step="supplier-form"]',
    cardPosition: "fixed-corner",
    icon: "BookText",
    noBackdrop: true,
    advanceOn: { type: "event", name: "tour:supplier-saved" },
    it: {
      title: "Compila i dati del fornitore",
      body: "Inserisci almeno il nome e la categoria; gli altri campi (contatti, note, dati fiscali) li aggiungi in seguito. I contratti, invece, NON si caricano qui: si caricano dentro ogni evento, nel tab «Contratti», sotto il fornitore collegato — così ogni matrimonio ha i suoi.",
      cta: "Clicca Salva quando sei pronto",
    },
    en: {
      title: "Fill in the supplier details",
      body: "Enter at least the name and category; other fields (contacts, notes, fiscal data) can be added later. Contracts, however, are NOT uploaded here: you upload them inside each event, in the «Contracts» tab under the linked vendor — so each wedding keeps its own.",
      cta: "Click Save when you're ready",
    },
  },

  // ── TASK 2: Crea il primo Evento ──────────────────────────────────────────

  {
    id: "nav-events",
    selector: '[data-tour-step="nav-events"]',
    cardPosition: "right",
    icon: "CalendarHeart",
    advanceOn: { type: "route", path: "/events" },
    it: {
      title: "Fornitore salvato! Ora crea un evento",
      body: "Ogni matrimonio è un progetto separato con la sua sezione dedicata per ospiti, fornitori, timeline, documenti e moodboard.",
      cta: "Clicca su Eventi nel menu",
    },
    en: {
      title: "Supplier saved! Now create an event",
      body: "Every wedding is a separate project with its own section for guests, vendors, timeline, documents and moodboard.",
      cta: "Click on Events in the menu",
    },
  },
  {
    id: "add-event-btn",
    selector: '[data-tour-step="add-event-btn"]',
    cardPosition: "below",
    icon: "CalendarHeart",
    advanceOn: { type: "click" },
    it: {
      title: "Crea il primo evento",
      body: "Apri il form e inserisci il nome della coppia, la data e il budget totale. Potrai aggiungere tutti i dettagli in seguito dalla scheda evento.",
      cta: "Clicca su Nuovo evento",
    },
    en: {
      title: "Create the first event",
      body: "Open the form and enter the couple's name, date and total budget. You can add all the details later from the event page.",
      cta: "Click on New event",
    },
  },
  {
    id: "event-form",
    selector: '[data-tour-step="event-form"]',
    cardPosition: "fixed-corner",
    icon: "CalendarHeart",
    noBackdrop: true,
    advanceOn: { type: "event", name: "tour:event-saved" },
    it: {
      title: "Compila i dati dell'evento",
      body: "Nome coppia e data sono obbligatori. Inserisci anche il budget totale per tenere traccia delle spese fin dal primo giorno.",
      cta: "Clicca Crea evento per continuare",
    },
    en: {
      title: "Fill in the event details",
      body: "Couple name and date are required. Also enter the total budget to track spending from day one.",
      cta: "Click Create event to continue",
    },
  },

  // ── TASK 3: Aggiungi la prima Coppia ──────────────────────────────────────

  {
    id: "nav-clients",
    selector: '[data-tour-step="nav-clients"]',
    cardPosition: "right",
    icon: "Users",
    advanceOn: { type: "route", path: "/clients" },
    it: {
      title: "Evento creato! Aggiungi le coppie",
      body: "Nella sezione Clienti tieni i dati di contatto di ogni coppia. Collegando un cliente a un evento hai tutto in un unico posto.",
      cta: "Clicca su Clienti nel menu",
    },
    en: {
      title: "Event created! Now add your couples",
      body: "In the Clients section you keep the contact details of each couple. Link a client to an event to have everything in one place.",
      cta: "Click on Clients in the menu",
    },
  },
  {
    id: "add-client-btn",
    selector: '[data-tour-step="add-client-btn"]',
    cardPosition: "below",
    icon: "Users",
    advanceOn: { type: "click" },
    it: {
      title: "Aggiungi la prima coppia",
      body: "Inserisci i nomi, l'email e la data del matrimonio. Puoi assegnare un tag CRM per seguire in quale fase si trova la coppia.",
      cta: "Clicca su Nuovo cliente",
    },
    en: {
      title: "Add your first couple",
      body: "Enter the names, email and wedding date. You can assign a CRM tag to track which stage the couple is at.",
      cta: "Click on New client",
    },
  },
  {
    id: "client-form",
    selector: '[data-tour-step="client-form"]',
    cardPosition: "fixed-corner",
    icon: "Users",
    noBackdrop: true,
    advanceOn: { type: "event", name: "tour:client-saved" },
    it: {
      title: "Compila i dati della coppia",
      body: "Il nome del partner è l'unico campo obbligatorio. Aggiungi l'email e la data del matrimonio per tenere tutto in ordine fin dal primo contatto.",
      cta: "Clicca Crea cliente per continuare",
    },
    en: {
      title: "Fill in the couple's details",
      body: "Partner name is the only required field. Add email and wedding date to keep everything organised from the very first contact.",
      cta: "Click Create client to continue",
    },
  },

  // ── TASK 4: Calendario ────────────────────────────────────────────────────

  {
    id: "nav-calendar",
    selector: '[data-tour-step="nav-calendar"]',
    cardPosition: "right",
    icon: "CalendarDays",
    advanceOn: { type: "route", path: "/calendar" },
    it: {
      title: "Coppia salvata! Esplora il Calendario",
      body: "Il calendario aggregato mostra tutti i tuoi appuntamenti, scadenze ed eventi in un'unica vista mensile. Non perdere mai una data importante.",
      cta: "Clicca su Calendario nel menu",
    },
    en: {
      title: "Couple saved! Explore the Calendar",
      body: "The aggregated calendar shows all your appointments, deadlines and events in a single monthly view. Never miss an important date.",
      cta: "Click on Calendar in the menu",
    },
  },

  // ── TASK 5: Impostazioni ──────────────────────────────────────────────────

  {
    id: "nav-settings",
    selector: '[data-tour-step="nav-settings"]',
    cardPosition: "right",
    icon: "Settings",
    advanceOn: { type: "route", path: "/settings" },
    it: {
      title: "Configura le Impostazioni",
      body: "Qui personalizzi il nome dell'agenzia, carichi il logo e gestisci l'abbonamento. Puoi anche cambiare lingua e notifiche email.",
      cta: "Clicca su Impostazioni nel menu",
    },
    en: {
      title: "Configure your Settings",
      body: "Here you customise your agency name, upload a logo and manage your subscription. You can also change language and email notifications.",
      cta: "Click on Settings in the menu",
    },
  },
  // ── TASK 6: Dashboard ─────────────────────────────────────────────────────

  {
    id: "nav-dashboard",
    selector: '[data-tour-step="nav-dashboard"]',
    cardPosition: "right",
    icon: "LayoutDashboard",
    advanceOn: { type: "route", path: "/dashboard" },
    it: {
      title: "Esplora la Dashboard",
      body: "Qui trovi in un colpo d'occhio gli eventi attivi, i pagamenti in scadenza e le attività da completare. Torna qui ogni volta che vuoi una visione d'insieme.",
      cta: "Clicca su Dashboard per continuare",
    },
    en: {
      title: "Explore the Dashboard",
      body: "Here you find active events, upcoming payments and tasks at a glance. Come back here whenever you want a full overview.",
      cta: "Click Dashboard to continue",
    },
  },

  // ── TASK 7: Il workspace dell'evento ──────────────────────────────────────
  // Step informativi (fixed-corner, avanzamento manuale): non dipendono da un
  // evento esistente né da un elemento in pagina, quindi non si bloccano mai.

  {
    id: "ai-event-workspace",
    cardPosition: "fixed-corner",
    icon: "CalendarHeart",
    noBackdrop: true,
    advanceOn: { type: "next" },
    it: {
      title: "Dentro l'evento c'è tutto",
      body: "Aprendo un evento trovi i tab dedicati: Panoramica, Budget, Fornitori, Pagamenti, Ospiti, Attività, Timeline, Documenti, Tavoli, Contratti, Moodboard e Portale. Ogni matrimonio ha il suo spazio completo.",
    },
    en: {
      title: "Everything lives inside the event",
      body: "Open an event to find dedicated tabs: Overview, Budget, Vendors, Payments, Guests, Tasks, Timeline, Documents, Tables, Contracts, Moodboard and Portal. Every wedding has its own complete workspace.",
    },
  },

  // ── TASK 8: Le funzioni AI ─────────────────────────────────────────────────

  {
    id: "ai-generate",
    cardPosition: "fixed-corner",
    icon: "Sparkles",
    noBackdrop: true,
    advanceOn: { type: "next" },
    it: {
      title: "Aurea lavora per te con l'AI",
      body: "Cerca il pulsante «Genera con AI» nei tab Timeline e Attività (crea programma e checklist), in Impostazioni → Email (scrive le email mantenendo i segnaposto), e nei riepiloghi Catering e Budget. Tu rivedi e confermi: niente viene salvato senza il tuo ok.",
    },
    en: {
      title: "Aurea works for you with AI",
      body: "Look for the «Generate with AI» button in the Timeline and Tasks tabs (it builds the schedule and checklist), in Settings → Email (it writes emails keeping the placeholders), and in the Catering and Budget summaries. You always review and confirm: nothing is saved without your approval.",
    },
  },
  {
    id: "ai-intelligence",
    cardPosition: "fixed-corner",
    icon: "BrainCircuit",
    noBackdrop: true,
    advanceOn: { type: "next" },
    it: {
      title: "Wedding Intelligence: anticipa i problemi",
      body: "All'apertura di ogni evento, la Panoramica segnala da sola le criticità: pagamenti scaduti, budget sforato, contratti da confermare, RSVP in attesa. Ogni avviso ha un pulsante che ti porta dritto dove agire. Funziona sempre, anche senza AI.",
    },
    en: {
      title: "Wedding Intelligence: stay ahead",
      body: "When you open an event, the Overview surfaces issues on its own: overdue payments, budget overruns, contracts to confirm, pending RSVPs. Each alert has a button taking you straight to where to act. It always works, even without AI.",
    },
  },
  {
    id: "ai-command",
    cardPosition: "fixed-corner",
    icon: "MessagesSquare",
    noBackdrop: true,
    advanceOn: { type: "next" },
    it: {
      title: "«Chiedi ad Aurea» (Premium)",
      body: "Il pulsante flottante in basso a destra apre l'assistente: fai domande sui tuoi dati in linguaggio naturale — «quali pagamenti sono in scadenza?», «quanti invitati confermati?» — e ottieni la risposta sui tuoi eventi reali.",
    },
    en: {
      title: "«Ask Aurea» (Premium)",
      body: "The floating button at the bottom-right opens the assistant: ask questions about your data in plain language — «which payments are due?», «how many confirmed guests?» — and get answers from your real events.",
    },
  },
  {
    id: "ai-portal",
    cardPosition: "fixed-corner",
    icon: "Share2",
    noBackdrop: true,
    advanceOn: { type: "next" },
    it: {
      title: "Portale Cliente (Premium)",
      body: "Dal tab «Portale» generi un link brandizzato che la coppia apre per vedere il proprio matrimonio: programma, moodboard, documenti condivisi e stato pagamenti. Decidi tu quali sezioni mostrare.",
    },
    en: {
      title: "Client Portal (Premium)",
      body: "From the «Portal» tab you generate a branded link the couple opens to see their own wedding: schedule, moodboard, shared documents and payment status. You choose which sections to show.",
    },
  },

  // ── Finale ─────────────────────────────────────────────────────────────────

  {
    id: "tour-finale",
    cardPosition: "fixed-corner",
    icon: "PartyPopper",
    noBackdrop: true,
    advanceOn: { type: "next" },
    it: {
      title: "Sei pronto!",
      body: "Hai visto le funzioni principali di Aurea Pro. Per ogni dettaglio consulta il Manuale d'uso, raggiungibile dal menu in qualsiasi momento. Buon lavoro!",
      next: "Inizia a usare Aurea",
    },
    en: {
      title: "You're all set!",
      body: "You've seen the main features of Aurea Pro. For every detail, check the User Manual, reachable from the menu at any time. Enjoy!",
      next: "Start using Aurea",
    },
  },
];
